import { Storage } from "@ionic/storage-angular";
import {
  saveRecord,
  saveRecords,
  getByValue,
  getByValues,
  getFilteredByMultiValue,
  getFilteredByValue,
  getFilteredByValues,
  remove,
  removeMultiple,
  patchRecord,
  patchRecords,
} from "../utils/array.repository.utils";
import { BaseStorageRepository } from "./base.storage.repository";

export abstract class ArrayStorageRepository<T> extends BaseStorageRepository<
  T[]
> {
  primaryKey: string;

  constructor(storage: Storage, accessor: string, primaryKey: string) {
    super(storage, accessor);
    this.primaryKey = primaryKey;
  }

  private setter = (value: T[]) => super.set(value);

  public override async get(): Promise<T[]> {
    return (await super.get()) || [];
  }

  public getById(id: string): Promise<T> {
    return this.getByValue(this.primaryKey, id);
  }

  public getAsArray(): Promise<T[]> {
    return this.get();
  }

  public async getAsMap(): Promise<{ [key: string]: T }> {
    const arr = (await this.get()) || [];
    return arr.reduce(
      (acc: any, item: any) => ({ ...acc, [item[this.primaryKey]]: item }),
      {},
    );
  }

  public async saveRecord(key: string | string[], record: T): Promise<T> {
    const previous = await this.get();
    return saveRecord(key, record, this.setter, previous);
  }

  public async saveRecords(key: string | string[], records: T[]): Promise<T[]> {
    const previous = await this.get();
    return saveRecords(key, records, this.setter, previous);
  }

  public async patchRecords(
    key: any | any[],
    value: any | any[],
    fieldsToPatch: Partial<T>,
  ): Promise<T[]> {
    const previous = await this.get();
    return patchRecords(
      this.primaryKey,
      key,
      value,
      fieldsToPatch,
      this.setter,
      previous,
    );
  }

  public async patchRecord(
    key: any | any[],
    value: any | any[],
    fieldsToPatch: Partial<T>,
  ): Promise<T> {
    const previous = await this.get();
    return patchRecord(
      this.primaryKey,
      key,
      value,
      fieldsToPatch,
      this.setter,
      previous,
    );
  }

  public async getLength(): Promise<number> {
    const records = await this.get();
    return records.length;
  }

  public async getByValue(key: string, value: any): Promise<T> {
    const records = await this.get();
    return getByValue(key, value, records);
  }

  public async getByValues(keys: string[], values: any[]): Promise<T> {
    const records = await this.get();
    return getByValues(keys, values, records);
  }

  public async getFilteredByValue(key: string, value: any): Promise<T[]> {
    const records = await this.get();
    return getFilteredByValue(key, value, records);
  }

  public async getFilteredByMultiValue(
    key: string,
    values: any[],
  ): Promise<T[]> {
    const records = await this.get();
    return getFilteredByMultiValue(key, values, records);
  }

  public async getFilteredByValues(
    keys: string[],
    values: any[],
  ): Promise<T[]> {
    const records = await this.get();
    return getFilteredByValues(keys, values, records);
  }

  public async remove(key: string, value: any): Promise<T[]> {
    const records = await this.get();
    return remove(key, value, this.setter, records);
  }

  public async removeMultiple(key: string, values: any[]): Promise<T[]> {
    const records = await this.get();
    return removeMultiple(key, values, this.setter, records);
  }
}
